// PayPal JS SDK loader. Loads the modern SDK with the components we use:
//   buttons      → PayPal + Pay Later wallet button
//   card-fields  → Advanced Card Processing (inline hosted card form)
//   messages     → Pay Later messaging banner
// The script is loaded once per client-id and the resolved promise is cached.
// Reference: https://developer.paypal.com/sdk/js/configuration/
import type { Locale } from './api/types'

/** Minimal surface of the global `paypal` namespace we rely on. */
export interface PayPalNamespace {
  Buttons: (opts: Record<string, unknown>) => {
    isEligible?: () => boolean
    render: (selector: string | HTMLElement) => Promise<void>
    close?: () => void
  }
  CardFields: (opts: Record<string, unknown>) => {
    isEligible: () => boolean
    NameField: (o?: Record<string, unknown>) => { render: (s: string | HTMLElement) => Promise<void> }
    NumberField: (o?: Record<string, unknown>) => { render: (s: string | HTMLElement) => Promise<void> }
    ExpiryField: (o?: Record<string, unknown>) => { render: (s: string | HTMLElement) => Promise<void> }
    CVVField: (o?: Record<string, unknown>) => { render: (s: string | HTMLElement) => Promise<void> }
    submit: (data?: Record<string, unknown>) => Promise<void>
  }
  Messages?: (opts: Record<string, unknown>) => { render: (s: string | HTMLElement) => Promise<void> }
  FUNDING?: Record<string, string>
  getFundingSources?: () => string[]
  rememberFunding?: (sources: string[]) => void
  // Wallets (dedicated components — only present when applepay/googlepay are in `components`).
  Googlepay?: () => {
    config: () => Promise<any>
    confirmOrder: (o: { orderId: string; paymentMethodData: any }) => Promise<{ status: string }>
  }
  Applepay?: () => {
    config: () => Promise<any>
    validateMerchant: (o: { validationUrl: string }) => Promise<{ merchantSession: any }>
    confirmOrder: (o: { orderId: string; token: any; billingContact?: any }) => Promise<any>
  }
  version?: string
}

declare global {
  interface Window {
    paypal?: PayPalNamespace
    google?: any
    ApplePaySession?: any
  }
}

/** Load the Google Pay JS API (pay.js). Resolves once `window.google.payments` is ready. */
let googlePayApi: Promise<void> | null = null
export function loadGooglePayApi(): Promise<void> {
  if (googlePayApi) return googlePayApi
  googlePayApi = new Promise<void>((resolve, reject) => {
    if (window.google?.payments?.api) return resolve()
    const s = document.createElement('script')
    s.src = 'https://pay.google.com/gp/p/js/pay.js'
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => {
      googlePayApi = null
      reject(new Error('Failed to load Google Pay API'))
    }
    document.head.appendChild(s)
  })
  return googlePayApi
}

const PAYPAL_LOCALE: Record<Locale, string> = { it: 'it_IT', en: 'en_US', de: 'de_DE' }

// Keyed by the full script src so switching client-id / locale reloads correctly.
const cache = new Map<string, Promise<PayPalNamespace>>()

function buildSrc(clientId: string, currency: string, intent: string, locale: Locale): string {
  const params = new URLSearchParams({
    'client-id': clientId,
    currency: currency || 'EUR',
    intent: (intent || 'capture').toLowerCase(),
    // buttons → PayPal + Pay Later (rendered as explicit funding sources, so no card button);
    // card-fields → inline Advanced Card form; applepay/googlepay → wallet sheets; messages → banner.
    // NB: do NOT set disable-funding=card — it also makes CardFields.isEligible() false.
    components: 'buttons,card-fields,applepay,googlepay,messages',
    'enable-funding': 'paylater',
    locale: PAYPAL_LOCALE[locale] ?? 'it_IT',
  })
  return `https://www.paypal.com/sdk/js?${params.toString()}`
}

/**
 * Load the PayPal SDK and resolve with the `paypal` namespace.
 * Rejects if no client-id is configured or the script fails to load.
 */
export function loadPayPal(
  clientId: string,
  opts: { currency?: string; intent?: string; locale?: Locale } = {},
): Promise<PayPalNamespace> {
  if (!clientId) return Promise.reject(new Error('PayPal client-id missing'))
  const { currency = 'EUR', intent = 'capture', locale = 'it' } = opts
  const src = buildSrc(clientId, currency, intent, locale)

  const cached = cache.get(src)
  if (cached) return cached

  const promise = new Promise<PayPalNamespace>((resolve, reject) => {
    // Already present (e.g. HMR or a prior load with the same src).
    const existing = document.querySelector<HTMLScriptElement>(`script[data-paypal-sdk="${src}"]`)
    if (existing && window.paypal) {
      resolve(window.paypal)
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.dataset.paypalSdk = src
    s.onload = () => {
      if (window.paypal) resolve(window.paypal)
      else reject(new Error('PayPal SDK loaded but window.paypal is undefined'))
    }
    s.onerror = () => {
      cache.delete(src) // allow a later retry
      reject(new Error('Failed to load PayPal SDK'))
    }
    document.head.appendChild(s)
  })

  cache.set(src, promise)
  return promise
}
